/**
 * V12 数据中心 — 服务端同步客户端
 *
 * 职责：
 * - pullAll()  — 登录时拉取服务器最新数据，对比本地
 * - pushOne()  — 写操作后 fire-and-forget 推送到服务器
 * - pushOneAndWait() — 关键提交等待服务器保存完成
 * - pushAll()  — 退出时等待所有 pending PUT 完成
 * - _dirty 标记 — 推送失败的模块，下次重试
 *
 * 同步策略: 服务器数据为准；本地写入仍会推送到服务器
 *
 * @see design/v12-user-data-center/spec.md §FR-006
 */

import { dataProxy } from "./data-proxy";
import { DATA_MODULES, type DataModule } from "./data-registry";
import {
  isLoggedIn,
  getCurrentUserOpenid,
  setOnUserChange,
  setOnBeforeUserClear,
} from "./user-context";
import { setOnDataWrite } from "./data-proxy";

// ============================================================
// 类型
// ============================================================

interface ModuleMeta {
  _syncAt: number;
  _size: number;
}

interface PullAllResponse {
  ok: boolean;
  modules: Record<string, ModuleMeta>;
}

// ============================================================
// 内部状态
// ============================================================

/** 本地 _syncAt 追踪（模块 → 最后同步时间戳） */
const localSyncMeta: Record<string, number> = {};

/** 推送失败的模块集合（待重试） */
const _dirty: Set<DataModule> = new Set();

/** 进行中的推送 Promise（用于 pushAll 等待） */
const pendingPushes: Map<DataModule, Promise<void>> = new Map();

/** 推送期间再次写入的模块（当前请求完成后补发最新数据） */
const queuedPushes: Set<DataModule> = new Set();

/** 是否正在拉取（防止并发） */
let isPulling = false;

/** 是否正在同步（防止 pull 触发 push 回环） */
let isSyncing = false;
let syncUserOpenid: string | null = null;

// ============================================================
// HTTP 工具
// ============================================================

function getAuthToken(): string | null {
  return localStorage.getItem("battlecode-auth-token");
}

function getApiBase(): string {
  if (import.meta.env.DEV) {
    return "http://localhost:3001";
  }
  return "";  // 生产环境相对路径
}

async function apiGet(path: string): Promise<Response> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return fetch(`${getApiBase()}${path}`, { headers });
}

async function apiPut(path: string, body: unknown): Promise<Response> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return fetch(`${getApiBase()}${path}`, {
    method: "PUT",
    headers,
    body: JSON.stringify(body),
  });
}

// ============================================================
// 拉取（登录时）
// ============================================================

/**
 * pullAll() — 登录时调用，拉取服务器最新数据。
 *
 * 策略：
 * 1. GET /api/user/data → 获取服务器端所有模块的 _syncAt
 * 2. 对比本地数据:
 *    - 本地无此模块 + 服务器有 → 自动拉取
 *    - 本地有 + 服务器无 → 自动推送（首次上传）
 *    - 服务器有此模块 → 服务器为准，拉取覆盖本地
 *    - _syncAt 相同 → 跳过
 * 3. 不弹冲突提示，不用本地数据覆盖已有的服务器数据
 */
export async function pullAll(): Promise<void> {
  if (!isLoggedIn()) return;
  if (isPulling) return;

  isPulling = true;
  isSyncing = true;

  try {
    // 1. 获取服务器元数据
    const resp = await apiGet("/api/user/data");
    if (resp.status === 401) {
      console.warn("[SyncClient] pullAll: 未登录，跳过同步");
      return;
    }
    if (!resp.ok) {
      console.warn(`[SyncClient] pullAll: HTTP ${resp.status}`);
      return;
    }

    const { modules: serverMeta } = (await resp.json()) as PullAllResponse;
    if (!serverMeta) return;

    // 2. 对比每个模块
    const toPull: DataModule[] = [];
    const toPush: DataModule[] = [];

    for (const [moduleName, sMeta] of Object.entries(serverMeta)) {
      const localSync = localSyncMeta[moduleName] || 0;
      const hasLocal = dataProxy.has(moduleName as DataModule);

      if (!hasLocal && sMeta._syncAt > 0) {
        // 本地无此模块，服务器有 → 拉取
        toPull.push(moduleName as DataModule);
      } else if (hasLocal && sMeta._syncAt === 0) {
        // 本地有，服务器无 → 推送
        toPush.push(moduleName as DataModule);
      } else if (hasLocal && sMeta._syncAt > 0) {
        // 服务器已有数据时始终以服务器为准，不弹窗、不反向覆盖服务器。
        if (sMeta._syncAt !== localSync) {
          toPull.push(moduleName as DataModule);
        }
        // _syncAt 相同 → 跳过
      }
    }

    // 3. 执行拉取（在拉取期间 isSyncing=true，防止触发回环推送）
    for (const moduleName of toPull) {
      await pullOne(moduleName);
    }

    // 4. 执行推送
    isSyncing = false;
    for (const moduleName of toPush) {
      await pushOneInternal(moduleName, /* wait */ true);
    }

    // 5. 检查本地有但服务器元数据中没有的模块 → 推送。
    // 不能只遍历 localSyncMeta：刷新页面后它为空，但账号本地仍可能有待首次上传的数据。
    for (const moduleName of DATA_MODULES) {
      if (!serverMeta[moduleName] && dataProxy.has(moduleName)) {
        await pushOneInternal(moduleName, /* wait */ true);
      }
    }

    console.info("[SyncClient] pullAll complete:", {
      pulled: toPull.length,
      pushed: toPush.length,
    });
  } catch (e) {
    // dev 模式下后端未启动时静默降级，不污染 console
    const msg = (e as Error).message || "";
    if (import.meta.env.DEV && (msg.includes("Failed to fetch") || msg.includes("NetworkError"))) {
      // 3001 不可达，静默（不 warn）
    } else {
      console.warn("[SyncClient] pullAll failed:", msg);
    }
  } finally {
    isPulling = false;
    isSyncing = false;
  }
}

/** 从服务器拉取单个模块 */
async function pullOne(moduleName: DataModule): Promise<void> {
  try {
    const resp = await apiGet(`/api/user/data/${moduleName}`);
    if (resp.status === 404) return;  // 服务器无数据
    if (!resp.ok) {
      console.warn(`[SyncClient] pullOne ${moduleName}: HTTP ${resp.status}`);
      return;
    }

    const body = await resp.json() as { ok: boolean; module: string; data: unknown; _syncAt: number };
    if (!body.ok || !body.data) return;

    dataProxy.set(moduleName, body.data);
    localSyncMeta[moduleName] = body._syncAt;
  } catch (e) {
    console.warn(`[SyncClient] pullOne ${moduleName} failed:`, (e as Error).message);
  }
}

// ============================================================
// 推送（写操作后 fire-and-forget）
// ============================================================

/**
 * pushOne() — 写操作后调用，fire-and-forget 推送到服务器。
 * 如果之前有 _dirty 标记，先重试 dirty 模块。
 */
export function pushOne(moduleName: DataModule): void {
  if (!isLoggedIn()) return;

  // 先重试 dirty 模块
  if (_dirty.size > 0) {
    const dirty = [..._dirty];
    for (const m of dirty) {
      _dirty.delete(m);
      pushOneInternal(m, /* wait */ false);
    }
  }

  pushOneInternal(moduleName, /* wait */ false);
}

/**
 * 推送单个模块并等待服务器确认，供战斗提交等关键写入使用。
 * 若写入发生在上一请求期间，会继续等待补发的最新数据。
 */
export async function pushOneAndWait(moduleName: DataModule): Promise<void> {
  if (!isLoggedIn()) return;
  if (!pendingPushes.has(moduleName)) {
    pushOne(moduleName);
  }

  let pending = pendingPushes.get(moduleName);
  while (pending) {
    await pending;
    pending = pendingPushes.get(moduleName);
  }
}

/** 推送单个模块到服务器 */
async function pushOneInternal(moduleName: DataModule, wait: boolean): Promise<void> {
  if (pendingPushes.has(moduleName) && !wait) {
    // 已有进行中的推送，当前写入需在其完成后补发
    queuedPushes.add(moduleName);
    return;
  }

  const promise = (async () => {
    try {
      const data = dataProxy.get(moduleName);
      if (data === null) return;

      const resp = await apiPut(`/api/user/data/${moduleName}`, data);

      if (resp.status === 401) {
        console.warn("[SyncClient] pushOne: token expired, marking dirty");
        _dirty.add(moduleName);
        return;
      }
      if (resp.status === 413) {
        console.warn(`[SyncClient] pushOne ${moduleName}: data too large, skipping`);
        return;
      }
      if (!resp.ok) {
        console.warn(`[SyncClient] pushOne ${moduleName}: HTTP ${resp.status}, marking dirty`);
        _dirty.add(moduleName);
        return;
      }

      const body = await resp.json() as { ok: boolean; _syncAt: number };
      if (body.ok) {
        localSyncMeta[moduleName] = body._syncAt;
        _dirty.delete(moduleName);
      }
    } catch (e) {
      // 网络错误 → 静默 + _dirty
      _dirty.add(moduleName);
    } finally {
      pendingPushes.delete(moduleName);
      if (queuedPushes.delete(moduleName)) {
        void pushOneInternal(moduleName, /* wait */ false);
      }
    }
  })();

  pendingPushes.set(moduleName, promise);

  if (wait) {
    await promise;
  }
}

// ============================================================
// 退出时推送全部
// ============================================================

/**
 * pushAll() — 退出时调用，等待所有 pending PUT 完成。
 * 最多等 5 秒，超时放弃，_dirty 标记保留。
 */
export async function pushAll(): Promise<void> {
  if (!isLoggedIn()) return;
  if (pendingPushes.size === 0 && _dirty.size === 0) return;

  // 先推送所有 dirty 模块
  const dirty = [..._dirty];
  for (const m of dirty) {
    _dirty.delete(m);
    pushOneInternal(m, /* wait */ false);
  }

  // 等待所有 pending 推送完成（最多 5 秒）
  const allPromises = [...pendingPushes.values()];
  if (allPromises.length === 0) return;

  try {
    await Promise.race([
      Promise.all(allPromises),
      new Promise<void>((resolve) => setTimeout(resolve, 5000)),
    ]);
  } catch {
    // 超时放弃
  }

  // 未完成的标记为 dirty
  for (const [moduleName] of pendingPushes) {
    _dirty.add(moduleName);
  }

  console.info(`[SyncClient] pushAll complete, dirty: ${_dirty.size} modules`);
}

// ============================================================
// 初始化：注册钩子
// ============================================================

/** 写操作回调：每次 DataProxy.set() → 触发 pushOne */
function onDataWrite(moduleName: DataModule): void {
  // 拉取期间不推送（防止回环）
  if (isSyncing) return;
  // 更新本地 _syncAt
  localSyncMeta[moduleName] = Date.now();
  // fire-and-forget 推送
  pushOne(moduleName);
}

/** 用户切换回调 */
async function onUserChange(openid: string | null): Promise<void> {
  if (openid) {
    if (syncUserOpenid !== openid) {
      Object.keys(localSyncMeta).forEach((k) => delete localSyncMeta[k]);
      _dirty.clear();
      syncUserOpenid = openid;
    }
    // 登录/切换账号 → 等待账号云端 hydrate 完成
    await pullAll();
  } else {
    // 身份已清除；登出前推送由 setOnBeforeUserClear 负责
    Object.keys(localSyncMeta).forEach((k) => delete localSyncMeta[k]);
    _dirty.clear();
    syncUserOpenid = null;
  }
}

// 注册钩子
setOnDataWrite(onDataWrite);
setOnUserChange(onUserChange);
setOnBeforeUserClear(async () => {
  await pushAll();
});

// 如果已登录（页面刷新），自动拉取
if (isLoggedIn()) {
  syncUserOpenid = getCurrentUserOpenid();
  void pullAll();
}

console.info("[SyncClient] initialized");
